import type { TFunction } from 'i18next';
import api from '../../../services/api';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import { normalizeTransportRouteDetail, normalizeTransportRoutes, TransportRouteDetailDto, TransportRouteSummaryDto } from '../../../dto/transport';

export interface TransportRouteListFilters {
  search?: string;
  transportCompany?: string;
  products?: string;
  status?: string;
  orderDateFrom?: string;
  orderDateTo?: string;
  /** WP14: INBOUND (przychodzące) / OUTBOUND (wychodzące). */
  direction?: 'INBOUND' | 'OUTBOUND';
}

export async function listTransportRoutes(
  filters: TransportRouteListFilters = {},
): Promise<TransportRouteSummaryDto[]> {
  const response = await api.get('/transport-routes', { params: filters });
  return normalizeTransportRoutes(response.data, 'transportRoutes.list');
}

export async function listTransportRoutesForSelection(): Promise<TransportRouteSummaryDto[]> {
  const response = await api.get('/transport-routes');
  return normalizeTransportRoutes(response.data, 'transportRoutes.selection');
}

export async function fetchTransportRoute(transportId: string): Promise<TransportRouteDetailDto> {
  const response = await api.get(`/transport-routes/${encodeURIComponent(transportId)}`);
  return normalizeTransportRouteDetail(response.data, 'transportRoutes.detail');
}

export async function createTransportRoute(payload: Record<string, unknown>) {
  return api.post('/transport-routes', payload);
}

export async function updateTransportRoute(transportId: string, payload: Record<string, unknown>) {
  return api.put(`/transport-routes/${encodeURIComponent(transportId)}`, payload);
}

/** Update, then return the canonical detail read model rather than local payload assumptions. */
export async function updateTransportRouteAndRefetch(
  transportId: string,
  payload: Record<string, unknown>,
): Promise<TransportRouteDetailDto> {
  await updateTransportRoute(transportId, payload);
  return fetchTransportRoute(transportId);
}

/**
 * Delete a transport (shared or single-order). Transports live under
 * /transport-routes regardless of how many orders they carry, so the deletion
 * seam belongs here rather than under any order-scoped API module.
 */
/**
 * WP12 (1.16): a transport that WZ/PZ documents point at is deleted after a
 * confirmed warning (the links are removed with it). With `confirmation` the
 * shared operational-exception dialog asks for the reason; the confirmation
 * travels in the DELETE body. Returns false when the operator cancelled.
 */
export interface TransportMutationConfirmation {
  modal: Parameters<typeof submitWithOperationalException>[2];
  t: TFunction;
}

async function deleteWithConfirmation(
  url: string,
  confirmation?: TransportMutationConfirmation,
) {
  if (!confirmation) return api.delete(url);
  return submitWithOperationalException(
    (body) => api.delete(url, { data: body }),
    {},
    confirmation.modal,
    confirmation.t,
  );
}

export async function deleteTransportRoute(
  transportId: string,
  confirmation?: TransportMutationConfirmation,
): Promise<boolean> {
  const response = await deleteWithConfirmation(
    `/transport-routes/${encodeURIComponent(transportId)}`,
    confirmation,
  );
  return response !== null;
}

export type UnlinkTransportOrderResult =
  | { deleted: true; route: null }
  | { deleted: false; route: TransportRouteDetailDto }
  | { cancelled: true; deleted: false; route: null };

/**
 * Unlink through the canonical TransportOrderAllocation endpoint. If the
 * transport survives, immediately refetch its canonical detail projection.
 */
export async function unlinkTransportOrderAndRefetch(
  transportId: string,
  orderId: string,
  confirmation?: TransportMutationConfirmation,
): Promise<UnlinkTransportOrderResult> {
  const response = await deleteWithConfirmation(
    `/transport-routes/${encodeURIComponent(transportId)}/orders/${encodeURIComponent(orderId)}`,
    confirmation,
  );
  if (response === null) return { cancelled: true, deleted: false, route: null };
  const deleted = response.data?.deleted === true;

  if (deleted) return { deleted: true, route: null };
  return { deleted: false, route: await fetchTransportRoute(transportId) };
}

/** Duplicate the persisted plan; the caller navigates to its canonical detail. */
export async function duplicateTransportRoute(transportId: string): Promise<TransportRouteDetailDto> {
  const response = await api.post(`/transport-routes/${encodeURIComponent(transportId)}/duplicate`);
  return normalizeTransportRouteDetail(response.data, 'transportRoutes.duplicate');
}
